import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  Stack,
  Avatar,
  Tooltip,
  FormControl,
  InputLabel,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";

export default function VariantForm({
  producto,
  tipo,
  setTipo,
  valor,
  setValor,
  precio,
  setPrecio,
  ivaPorc,
  setIvaPorc,
  stockMin,
  setStockMin,
  imagen,
  setImagen,
  variantes,
  setVariantes,
  variantesExistentes,
  tipoPermitido,
  resumen,
  ivaValorPreview,
  addVariant,
  deleteVariant,
  saveAll,
  saving,
  TIPOS,
  IVA_OPTIONS,
  stockMode,
  setStockMode,
  baseVariantId,
  setBaseVariantId,
  conversionQty,
  setConversionQty,
}) {
  const normalizeMode = (mode) => {
    if (mode === "DERIVED") return "DERIVED_VARIANT";
    return mode || "OWN";
  };

  const ownStockVariants = variantesExistentes.filter((v) => {
    const mode = normalizeMode(v.stock_mode);
    return mode === "OWN" && !v.base_variant && !v.base_variant_id;
  });

  const getBaseLabel = (id) => {
    const found = variantesExistentes.find((v) => String(v.id) === String(id));
    if (!found) return "-";
    return found.value || found.name || found.sku || "-";
  };

  const getModeLabel = (mode) => {
    const m = normalizeMode(mode);
    if (m === "DERIVED_VARIANT") return "Derivada de variante";
    if (m === "DERIVED_PRODUCT") return "Derivada del producto";
    return "Stock propio";
  };

  const getModeColor = (mode) => {
    const m = normalizeMode(mode);
    if (m === "DERIVED_VARIANT") return "warning";
    if (m === "DERIVED_PRODUCT") return "info";
    return "success";
  };

  return (
    <>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.2,
              borderRadius: 4,
              borderColor: "#dbe6f3",
              bgcolor: "#ffffff",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Avatar sx={{ bgcolor: "#e0f2fe", color: "#0369a1" }}>
                <Inventory2OutlinedIcon />
              </Avatar>
              <Box>
                <Typography fontWeight={800}>Producto seleccionado</Typography>
                <Typography variant="body2" sx={{ color: "#64748b" }}>
                  Verifica la información antes de crear las variantes.
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField label="Código" value={producto.code || ""} disabled fullWidth />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField label="Marca" value={producto.brand || ""} disabled fullWidth />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField label="Precio base" value={Number(producto.pvp1 || 0).toFixed(2)} disabled fullWidth />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 4, boxShadow: "none", border: "1px solid #dbe6f3", height: "100%" }}>
            <CardContent sx={{ p: 2.2 }}>
              <Typography fontWeight={800} sx={{ mb: 1.5 }}>
                Resumen de la tanda
              </Typography>

              <Stack spacing={1.2}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Variantes nuevas
                  </Typography>
                  <Typography fontWeight={800}>{resumen.total}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Stock propio
                  </Typography>
                  <Typography fontWeight={800}>{resumen.propias}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Derivadas
                  </Typography>
                  <Typography fontWeight={800}>{resumen.derivadas}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Desde producto
                  </Typography>
                  <Typography fontWeight={800}>{resumen.derivadasProducto}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Precio promedio
                  </Typography>
                  <Typography fontWeight={800}>${resumen.promedio}</Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Con imagen
                  </Typography>
                  <Typography fontWeight={800}>{resumen.conImagen}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {variantesExistentes.length > 0 && (
        <Paper
          sx={{
            p: 2.2,
            mb: 3,
            borderRadius: 4,
            border: "1px solid #fde68a",
            bgcolor: "#fffbeb",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Avatar sx={{ bgcolor: "#fef3c7", color: "#b45309" }}>
              <CategoryOutlinedIcon />
            </Avatar>
            <Box>
              <Typography fontWeight={800}>Variantes ya registradas</Typography>
              <Typography variant="body2" sx={{ color: "#92400e" }}>
                Si el producto ya representa la unidad principal, puedes crear paquetes o combos derivados del producto base.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1080 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#1565C0" }}>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Tipo</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Valor</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Precio</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>IVA %</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Stock mín.</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Inventario</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Base</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Contenido</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>SKU</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {variantesExistentes.map((v) => {
                  const mode = normalizeMode(v.stock_mode);
                  const baseName = v.base_variant_name || v.base_variant_value || getBaseLabel(v.base_variant_id || v.base_variant);

                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>{v.variant_type}</TableCell>
                      <TableCell>{v.value}</TableCell>
                      <TableCell>${Number(v.price).toFixed(2)}</TableCell>
                      <TableCell>{v.iva_porcentaje === 999 ? "No objeto" : `${v.iva_porcentaje}%`}</TableCell>
                      <TableCell>{v.stock_minimo ?? "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getModeLabel(mode)}
                          color={getModeColor(mode)}
                          variant={mode === "OWN" ? "outlined" : "filled"}
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        {mode === "DERIVED_VARIANT" ? baseName : mode === "DERIVED_PRODUCT" ? producto.name || "Producto base" : "-"}
                      </TableCell>
                      <TableCell>{mode !== "OWN" ? v.conversion_quantity || "1" : "-"}</TableCell>
                      <TableCell>{v.sku || "-"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <Paper
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 4,
          border: "1px solid #dbe6f3",
          bgcolor: "#ffffff",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Avatar sx={{ bgcolor: "#dbeafe", color: "#1565C0" }}>
            <SellOutlinedIcon />
          </Avatar>
          <Box>
            <Typography fontWeight={800}>Paso 2: agrega una variante</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Puedes crear colores, capacidades, materiales, kits o paquetes derivados.
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              Tipo de variante
            </Typography>
            <Select value={tipo} onChange={(e) => setTipo(e.target.value)} displayEmpty fullWidth>
              <MenuItem value="">
                <em>Selecciona un tipo</em>
              </MenuItem>

              {TIPOS.map((t) => (
                <MenuItem key={t} value={t} disabled={!!tipoPermitido && tipoPermitido !== t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              Valor
            </Typography>
            <TextField
              placeholder="Ej. Negro, 256GB, Kit x3"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              Precio
            </Typography>
            <TextField
              type="number"
              placeholder="0.00"
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              IVA
            </Typography>
            <Select value={ivaPorc} onChange={(e) => setIvaPorc(e.target.value)} fullWidth>
              {IVA_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>
                  {o === 999 ? "No objeto de IVA" : `${o}%`}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              IVA calculado
            </Typography>
            <TextField value={ivaValorPreview} fullWidth InputProps={{ readOnly: true }} />
          </Grid>

          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 4,
                borderColor: "#bfdbfe",
                bgcolor: "#f8fbff",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#dbeafe", color: "#1565C0" }}>
                  <SettingsSuggestOutlinedIcon />
                </Avatar>
                <Box>
                  <Typography fontWeight={800}>Configuración de inventario</Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Define si esta variante controla su propio stock o consume unidades de otro registro.
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Manejo de stock</InputLabel>
                    <Select
                      label="Manejo de stock"
                      value={stockMode}
                      onChange={(e) => {
                        setStockMode(e.target.value);
                        if (e.target.value === "OWN") {
                          setBaseVariantId("");
                          setConversionQty("1");
                        }
                        if (e.target.value === "DERIVED_PRODUCT") {
                          setBaseVariantId("");
                          setStockMin("");
                        }
                        if (e.target.value === "DERIVED_VARIANT") {
                          setStockMin("");
                        }
                      }}
                    >
                      <MenuItem value="OWN">Stock propio</MenuItem>
                      <MenuItem value="DERIVED_PRODUCT">Derivada del producto base</MenuItem>
                      <MenuItem value="DERIVED_VARIANT">Derivada de otra variante</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {stockMode === "OWN" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="number"
                      label="Stock mínimo"
                      placeholder="Opcional"
                      value={stockMin}
                      onChange={(e) => setStockMin(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                )}

                {stockMode === "DERIVED_PRODUCT" && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField label="Base" value={producto.name || "Producto actual"} fullWidth InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        type="number"
                        label="Cantidad contenida"
                        placeholder="Ej. 2, 3, 5, 10"
                        value={conversionQty}
                        onChange={(e) => setConversionQty(e.target.value)}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 3 }}>
                        Esta variante descontará {conversionQty || 0} unidad(es) del stock del producto base: {producto.name || "-"}.
                      </Alert>
                    </Grid>
                  </>
                )}

                {stockMode === "DERIVED_VARIANT" && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Variante base</InputLabel>
                        <Select
                          label="Variante base"
                          value={baseVariantId}
                          onChange={(e) => setBaseVariantId(e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Selecciona la base</em>
                          </MenuItem>

                          {ownStockVariants.map((v) => (
                            <MenuItem key={v.id} value={v.id}>
                              {v.value} {v.sku ? `- ${v.sku}` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        type="number"
                        label="Cantidad contenida"
                        placeholder="Ej. 2, 3, 5, 10"
                        value={conversionQty}
                        onChange={(e) => setConversionQty(e.target.value)}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity={baseVariantId ? "info" : "warning"} sx={{ borderRadius: 3 }}>
                        {baseVariantId
                          ? `Esta variante descontará ${conversionQty || 0} unidad(es) de la variante base seleccionada.`
                          : "Para usar este modo primero debe existir una variante con stock propio."}
                      </Alert>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              Imagen
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<ImageIcon />}
                sx={{ borderRadius: 3, textTransform: "none", minWidth: 170 }}
              >
                Subir imagen
                <input type="file" hidden accept="image/*" onChange={(e) => setImagen(e.target.files[0])} />
              </Button>

              <Box
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: 3,
                  border: "1px dashed #cbd5e1",
                  bgcolor: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {imagen ? (
                  <Box
                    component="img"
                    src={URL.createObjectURL(imagen)}
                    alt=""
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <ImageIcon sx={{ color: "#94a3b8" }} />
                )}
              </Box>

              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {imagen ? imagen.name : "Puedes dejarlo vacío si no deseas subir imagen"}
              </Typography>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="body2" sx={{ mb: 0.8, fontWeight: 700, color: "#334155" }}>
              Acción
            </Typography>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addVariant}
              fullWidth
              sx={{
                bgcolor: "#1565C0",
                borderRadius: 3,
                py: 1.5,
                textTransform: "none",
                fontWeight: 800,
                "&:hover": { bgcolor: "#0D47A1" },
              }}
            >
              Añadir a la lista
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {variantes.length > 0 && (
        <Paper
          sx={{
            p: 2.5,
            borderRadius: 4,
            border: "1px solid #dbe6f3",
            bgcolor: "#ffffff",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ color: "#1565C0" }}>
                Paso 3: revisa y guarda
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Aquí ves todas las variantes nuevas antes de enviarlas al servidor.
              </Typography>
            </Box>

            <Chip
              label={`${variantes.length} variante${variantes.length !== 1 ? "s" : ""} lista${variantes.length !== 1 ? "s" : ""}`}
              color="primary"
              sx={{ borderRadius: 2, fontWeight: 800 }}
            />
          </Stack>

          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1180 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#1565C0" }}>
                  {["Tipo", "Valor", "Precio", "IVA %", "IVA $", "Stock mín.", "Inventario", "Base", "Contenido", "Imagen", "Eliminar"].map((h) => (
                    <TableCell key={h} sx={{ color: "#fff", fontWeight: 800 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {variantes.map((v) => {
                  const mode = normalizeMode(v.stock_mode);

                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>{v.tipo}</TableCell>
                      <TableCell>{v.valor}</TableCell>
                      <TableCell>${v.precio.toFixed(2)}</TableCell>
                      <TableCell>{v.iva_porcentaje === 999 ? "No objeto" : `${v.iva_porcentaje}%`}</TableCell>
                      <TableCell>${v.iva_valor.toFixed(2)}</TableCell>
                      <TableCell>{mode === "OWN" ? v.stock_minimo ?? "-" : "-"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getModeLabel(mode)}
                          color={getModeColor(mode)}
                          variant={mode === "OWN" ? "outlined" : "filled"}
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        {mode === "DERIVED_VARIANT"
                          ? getBaseLabel(v.base_variant_id)
                          : mode === "DERIVED_PRODUCT"
                            ? producto.name || "Producto base"
                            : "-"}
                      </TableCell>
                      <TableCell>{mode !== "OWN" ? v.conversion_quantity : "-"}</TableCell>
                      <TableCell>
                        {v.imagen ? (
                          <Box
                            component="img"
                            src={URL.createObjectURL(v.imagen)}
                            alt=""
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: 2,
                              objectFit: "cover",
                              border: "1px solid #e2e8f0",
                            }}
                          />
                        ) : (
                          "–"
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Quitar de la lista">
                          <IconButton onClick={() => deleteVariant(v.id)}>
                            <DeleteIcon color="error" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => setVariantes([])}
              sx={{ borderRadius: 3, textTransform: "none", fontWeight: 700 }}
            >
              Limpiar lista
            </Button>

            <Button
              variant="contained"
              onClick={saveAll}
              disabled={saving}
              sx={{
                bgcolor: "#1565C0",
                borderRadius: 3,
                px: 3,
                textTransform: "none",
                fontWeight: 800,
                "&:hover": { bgcolor: "#0D47A1" },
              }}
            >
              {saving ? "Guardando..." : "Guardar variantes"}
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
}